/**
 * StockGrupoModal.jsx — Consulta (solo lectura) del stock de la empresa madre y/o sucursales del grupo.
 * Visible según los permisos inventario_productos.ver_stock_madre / ver_stock_sucursales.
 */
import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../App";
import { X, Search, Building2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 3 });

export default function StockGrupoModal({ onClose }) {
  const { token, activeEmpresaPropia } = useContext(AuthContext);
  const logo = activeEmpresaPropia?.slug || "";
  const headers = { Authorization: `Bearer ${token}` };
  const [empresas, setEmpresas] = useState([]);
  const [empresa, setEmpresa] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [filas, setFilas] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);

  useEffect(() => {
    if (!logo) return;
    fetch(`${API}/admin/stock-grupo/empresas?logo_tipo=${encodeURIComponent(logo)}`, { headers })
      .then(r => (r.ok ? r.json() : [])).then(setEmpresas);
    // eslint-disable-next-line
  }, [logo]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!logo) return;
    timer.current = setTimeout(async () => {
      setCargando(true); setError("");
      const q = new URLSearchParams({ logo_tipo: logo, page: "1", page_size: "200" });
      if (empresa) q.set("empresa", empresa);
      busqueda.split(/\s+/).filter(Boolean).forEach(t => q.append("terminos", t));
      try {
        const r = await fetch(`${API}/admin/stock-grupo?${q}`, { headers });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "No se pudo consultar el stock");
        setFilas(d.productos || []); setTotal(d.total || 0);
      } catch (e) { setError(e.message); setFilas([]); setTotal(0); }
      setCargando(false);
    }, 250);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [logo, empresa, busqueda]);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-arandu-dark-light border border-white/10 rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="font-heading text-lg text-white flex items-center gap-2"><Building2 className="w-5 h-5 text-cyan-400" /> Stock de la empresa madre / sucursales</h2>
            <p className="text-slate-400 text-sm">Consulta de solo lectura desde {activeEmpresaPropia?.nombre || logo}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {empresas.length === 0 && !cargando ? (
          <p className="text-sm text-slate-400 py-8 text-center">Esta empresa no pertenece a un grupo, o tu usuario no tiene empresas del grupo habilitadas para consultar.</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-2 mb-3">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
                <input value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por nombre, SKU, código de barras…"
                  className="w-full bg-arandu-dark border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400" />
              </div>
              <select value={empresa} onChange={e => setEmpresa(e.target.value)}
                className="bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-cyan-400">
                <option value="">Todas las empresas habilitadas</option>
                {empresas.map(e => <option key={e.slug} value={e.slug}>{e.nombre}{e.es_madre ? " (madre)" : ""}</option>)}
              </select>
            </div>
            {error && <p className="text-sm text-red-400 mb-2">{error}</p>}
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-xs text-slate-400">
                  <tr>
                    <th className="text-left p-2">Producto</th><th className="text-left p-2">SKU</th><th className="text-left p-2">Empresa</th>
                    <th className="p-2 text-right">Stock</th><th className="p-2 text-right">Reservado</th><th className="p-2 text-right">Disponible</th>
                  </tr>
                </thead>
                <tbody>
                  {cargando && <tr><td colSpan={6} className="p-4 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>}
                  {!cargando && filas.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-slate-500">Sin resultados</td></tr>}
                  {!cargando && filas.map(f => {
                    const disp = f.stock_actual - f.stock_reservado;
                    return (
                      <React.Fragment key={f.id}>
                        <tr className="border-t border-white/5 text-slate-200">
                          <td className="p-2">{f.nombre}{f.categoria && <span className="text-xs text-slate-500"> · {f.categoria}</span>}</td>
                          <td className="p-2 text-slate-400">{f.sku || f.codigo_barras || "—"}</td>
                          <td className="p-2">{f.empresa_nombre}{f.es_madre && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300">madre</span>}</td>
                          <td className="p-2 text-right">{fmt(f.stock_actual)} {f.unidad}</td>
                          <td className="p-2 text-right text-slate-400">{fmt(f.stock_reservado)}</td>
                          <td className={`p-2 text-right font-medium ${disp <= 0 ? "text-red-400" : disp <= f.stock_minimo ? "text-orange-300" : "text-emerald-400"}`}>{fmt(disp)}</td>
                        </tr>
                        {f.variantes.map((v, i) => (
                          <tr key={i} className="text-xs text-slate-400">
                            <td className="pl-6 py-1" colSpan={3}>↳ {v.texto}</td>
                            <td className="p-1 text-right">{fmt(v.stock_actual)}</td>
                            <td className="p-1 text-right">{fmt(v.stock_reservado)}</td>
                            <td className="p-1 text-right">{fmt(v.stock_actual - v.stock_reservado)}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-2">{total > filas.length ? `Mostrando ${filas.length} de ${total}. Afiná la búsqueda para ver el resto.` : `${total} producto(s)`}</p>
          </>
        )}
      </div>
    </div>
  );
}
